import * as React from 'react'
import * as TabsPrimitive from '@radix-ui/react-tabs'
import { cva, type VariantProps } from 'class-variance-authority'

import { cn } from '@/lib/utils'

export type TabsAppearance = 'underline' | 'embedded'
export type TabsSize = 'dense' | 'compact' | 'default' | 'comfortable'

type TabsStyle = {
  appearance: TabsAppearance
  size: TabsSize
}

const TabsStyleContext = React.createContext<TabsStyle>({
  appearance: 'underline',
  size: 'default',
})

const tabsListVariants = cva(
  'max-w-full items-end justify-start bg-transparent text-muted-foreground',
  {
    variants: {
      size: {
        dense: 'h-7',
        compact: 'h-9',
        default: 'h-10',
        comfortable: 'h-12',
      },
      layout: {
        scroll: 'inline-flex w-fit overflow-x-auto overflow-y-hidden',
        fill: 'flex w-full overflow-x-auto overflow-y-hidden [&>[role=tab]]:min-w-max [&>[role=tab]]:flex-1',
        wrap: 'flex !h-auto w-full flex-wrap overflow-visible',
      },
    },
    defaultVariants: {
      size: 'default',
      layout: 'scroll',
    },
  }
)

const tabsTriggerVariants = cva(
  "relative inline-flex flex-none cursor-pointer items-center gap-1.5 whitespace-nowrap outline-none focus-visible:outline-2 focus-visible:outline-offset-[-2px] focus-visible:outline-ring disabled:pointer-events-none disabled:text-muted-foreground [&_svg]:pointer-events-none [&_svg]:shrink-0 [&_svg:not([class*='size-'])]:size-4",
  {
    variants: {
      appearance: {
        underline:
          'border-b-2 border-transparent text-muted-foreground transition-[border-color,color] hover:text-foreground data-[state=active]:border-emphasis data-[state=active]:text-foreground',
        embedded:
          'border-0 bg-transparent text-muted-foreground transition-colors hover:text-foreground data-[state=active]:text-accentSecondary-foreground',
      },
      size: {
        dense: 'h-6 px-1.5 py-0 text-[13px] font-medium',
        compact: 'h-full px-2 py-1.5 text-sm font-medium',
        default: 'h-full px-3 py-2 text-sm font-medium',
        comfortable: 'h-full px-2.5 py-2 text-sm font-semibold sm:px-4 sm:text-base',
      },
      align: {
        center: 'justify-center',
        start: 'justify-start',
      },
    },
    defaultVariants: {
      appearance: 'underline',
      size: 'default',
      align: 'center',
    },
  }
)

export type TabsProps = React.ComponentProps<typeof TabsPrimitive.Root> & {
  appearance?: TabsAppearance
  size?: TabsSize
}

function Tabs({ className, appearance = 'underline', size = 'default', ...props }: TabsProps) {
  return (
    <TabsStyleContext.Provider value={{ appearance, size }}>
      <TabsPrimitive.Root
        data-slot="tabs"
        data-appearance={appearance}
        data-size={size}
        className={cn('flex flex-col gap-2', className)}
        {...props}
      />
    </TabsStyleContext.Provider>
  )
}

export type TabsListProps = React.ComponentProps<typeof TabsPrimitive.List> &
  VariantProps<typeof tabsListVariants>

function TabsList({ className, layout = 'scroll', size, ...props }: TabsListProps) {
  const context = React.useContext(TabsStyleContext)
  const resolvedSize = size ?? context.size

  return (
    <TabsPrimitive.List
      data-slot="tabs-list"
      data-layout={layout}
      data-size={resolvedSize}
      className={cn(tabsListVariants({ layout, size: resolvedSize }), className)}
      {...props}
    />
  )
}

export type TabsTriggerProps = React.ComponentProps<typeof TabsPrimitive.Trigger> &
  VariantProps<typeof tabsTriggerVariants>

function TabsTrigger({
  className,
  align = 'center',
  appearance,
  size,
  ...props
}: TabsTriggerProps) {
  const context = React.useContext(TabsStyleContext)
  const resolvedAppearance = appearance ?? context.appearance
  const resolvedSize = size ?? context.size

  return (
    <TabsPrimitive.Trigger
      data-slot="tabs-trigger"
      data-appearance={resolvedAppearance}
      data-size={resolvedSize}
      className={cn(
        tabsTriggerVariants({
          align,
          appearance: resolvedAppearance,
          size: resolvedSize,
        }),
        className
      )}
      {...props}
    />
  )
}

export type TabsContentProps = React.ComponentProps<typeof TabsPrimitive.Content>

function TabsContent({ className, ...props }: TabsContentProps) {
  return (
    <TabsPrimitive.Content
      data-slot="tabs-content"
      className={cn('flex-1 outline-none data-[state=inactive]:hidden', className)}
      {...props}
    />
  )
}

export { Tabs, TabsContent, TabsList, TabsTrigger, tabsListVariants, tabsTriggerVariants }
